// Immutable Board construction and queries. Spec: docs/mechanics.md §1-2, §11.
import { coordKey, hexRegion, compareCoord } from './hex.js';
export function createBoard(radius, holes = []) {
    const cells = new Map();
    for (const c of hexRegion(radius))
        cells.set(coordKey(c), 'in');
    for (const h of holes)
        cells.set(coordKey(h), 'hole');
    return {
        radius,
        cells,
        tiles: new Map(),
        tileAt: new Map(),
        nextId: 1,
        gravity: new Map(),
        wind: new Map(),
    };
}
export function loadLevel(level) {
    let board = createBoard(level.radius, level.holes);
    for (const g of level.gravity ?? []) {
        board = addGravity(board, g.coord, g.direction);
    }
    for (const w of level.wind ?? []) {
        board = addWind(board, w.coord, w.direction);
    }
    for (const t of level.tiles) {
        board = addTile(board, t.coord, t.color, t.state ?? { kind: 'normal' });
    }
    return board;
}
// W14 Ballast: pin a gravity arrow to an in-board cell. Cell must be 'in'
// (not a hole and not out-of-board). Each cell carries at most one direction;
// re-pinning the same cell overwrites. Mutating returns a new immutable Board.
export function addGravity(board, coord, direction) {
    const k = coordKey(coord);
    if (board.cells.get(k) !== 'in') {
        throw new Error(`addGravity: cell ${k} is not an in-board cell`);
    }
    const gravity = new Map(board.gravity);
    gravity.set(k, direction);
    return { ...board, gravity };
}
// W17 Gale: pin a wind source to an in-board cell. Same cell-must-be-in
// invariant as addGravity. Wind sources push tiles that land on the cell
// in the wind's direction (i.e., the cell at step(windCell, direction)).
// A cell can hold at most one wind source; re-pinning overwrites. A cell
// can simultaneously be a gravity cell AND a wind source (the engine
// resolves gravity first per slide.ts postSlideLoop, but practically
// these are different layout choices and both is rare).
export function addWind(board, coord, direction) {
    const k = coordKey(coord);
    if (board.cells.get(k) !== 'in') {
        throw new Error(`addWind: cell ${k} is not an in-board cell`);
    }
    const wind = new Map(board.wind);
    wind.set(k, direction);
    return { ...board, wind };
}
export function addTile(board, coord, color, state = { kind: 'normal' }) {
    const k = coordKey(coord);
    if (board.cells.get(k) !== 'in') {
        throw new Error(`addTile: cell ${k} is not an in-board cell`);
    }
    if (board.tileAt.has(k)) {
        throw new Error(`addTile: cell ${k} already has a tile`);
    }
    const id = board.nextId;
    const tile = { id, coord, color, state };
    const tiles = new Map(board.tiles);
    tiles.set(id, tile);
    const tileAt = new Map(board.tileAt);
    tileAt.set(k, id);
    return { ...board, tiles, tileAt, nextId: id + 1 };
}
export function removeTile(board, id) {
    const t = board.tiles.get(id);
    if (!t)
        return board;
    const tiles = new Map(board.tiles);
    tiles.delete(id);
    const tileAt = new Map(board.tileAt);
    tileAt.delete(coordKey(t.coord));
    return { ...board, tiles, tileAt };
}
export function moveTile(board, id, to) {
    const t = board.tiles.get(id);
    if (!t)
        throw new Error(`moveTile: no tile ${id}`);
    const fromK = coordKey(t.coord);
    const toK = coordKey(to);
    if (fromK === toK)
        return board;
    if (board.cells.get(toK) !== 'in')
        throw new Error(`moveTile: target ${toK} not in-board`);
    if (board.tileAt.has(toK))
        throw new Error(`moveTile: target ${toK} occupied`);
    const tiles = new Map(board.tiles);
    tiles.set(id, { ...t, coord: to });
    const tileAt = new Map(board.tileAt);
    tileAt.delete(fromK);
    tileAt.set(toK, id);
    return { ...board, tiles, tileAt };
}
export function replaceTile(board, id, patch) {
    const t = board.tiles.get(id);
    if (!t)
        throw new Error(`replaceTile: no tile ${id}`);
    const tiles = new Map(board.tiles);
    tiles.set(id, { ...t, ...patch });
    return { ...board, tiles };
}
export function getTile(board, coord) {
    const id = board.tileAt.get(coordKey(coord));
    return id ? board.tiles.get(id) ?? null : null;
}
export function listTiles(board) {
    return [...board.tiles.values()].sort((a, b) => compareCoord(a.coord, b.coord));
}
export function cellStatus(board, coord) {
    return board.cells.get(coordKey(coord)) ?? 'out';
}
export function isBlocker(board, coord) {
    const st = board.cells.get(coordKey(coord));
    if (st === undefined || st === 'out' || st === 'hole')
        return true;
    return board.tileAt.has(coordKey(coord));
}
// Deterministic canonical hash of board state. Used by solver transposition table.
// Canonicalizes only on tile positions/colors/states; NOT rotation-folded here (solver handles that).
export function boardHash(board) {
    const parts = [`R${board.radius}`];
    const holes = [];
    for (const [k, st] of board.cells)
        if (st === 'hole')
            holes.push(k);
    holes.sort();
    parts.push(`H:${holes.join('|')}`);
    // W14 Ballast: gravity placements are level-static but level-distinguishing.
    // Including them keeps the transposition table correct across levels with
    // different gravity layouts; within a single level the segment is constant
    // so it doesn't affect cache hit rate.
    if (board.gravity.size > 0) {
        const gravityEntries = [];
        for (const [k, dir] of board.gravity)
            gravityEntries.push(`${k}:${dir}`);
        gravityEntries.sort();
        parts.push(`G:${gravityEntries.join('|')}`);
    }
    // W17 Gale: wind placements are also level-distinguishing (same logic
    // as gravity — different layouts must hash differently in the solver
    // transposition table).
    if (board.wind.size > 0) {
        const windEntries = [];
        for (const [k, dir] of board.wind)
            windEntries.push(`${k}:${dir}`);
        windEntries.sort();
        parts.push(`W:${windEntries.join('|')}`);
    }
    const tiles = listTiles(board).map((t) => {
        const s = t.state;
        const stateTag = s.kind === 'normal' ? 'N' :
            s.kind === 'charged' ? `C${s.chainDepth}` :
                s.kind === 'locked' ? `L${s.key}` :
                    s.kind === 'mixer' ? 'M' :
                        s.kind === 'reactive' ? 'R' :
                            `F${s.remaining}`;
        return `${t.coord.q},${t.coord.r}:${t.color}:${stateTag}`;
    });
    parts.push(`T:${tiles.join('|')}`);
    return parts.join(';');
}
